// ///////////////////
// Request.TreeNavigation
// ///////////////////

import { TranslatedEpdType } from "@/providers/Localization";

export type InspectTreeNavigationRequestType = { typeIdentifier: string };

// ///////////////////
// Response.TreeNavigation
// ///////////////////

export type InspectTreeNavigationType = {
  id: string;
  name: string;
  constructions?: InspectTreeNavigationConstructionType;
  epds?: InspectTreeNavigationEpdType;
};

export type InspectTreeNavigationConstructionType = {
  [key: string]: {
    id: string;
    name: string;
    mappingName?: string;
    constructions?: InspectTreeNavigationConstructionType;
    epds?: InspectTreeNavigationEpdType;
  };
};

export type InspectTreeNavigationEpdType = {
  id: string;
  mappingName?: string;
  name: string;
};

// ///////////////////
// Request.Data
// ///////////////////

export type InspectBuildingProjectTypeRequestType = {
  typeIdentifier: string;
  indicatorTypeMappingId?: string;
};
export type InspectBuildingProjectConstructionRequestType = {
  constructionId: string;
  indicatorTypeMappingId?: string;
};
export type InspectBuildingProjectMaterialRequestType = {
  materialId: string;
  indicatorTypeMappingId?: string;
};
export type InspectBuildingProjectEpdRequestType = {
  epdId: string;
  indicatorTypeMappingId?: string;
};
export type InspectBuildingProjectIdType = string;

// ///////////////////
// Response.Data
// ///////////////////

export type InspectDataType = {
  emissionByStages: InspectEmissionByStagesType;
  emissionData: InspectEmissionDataType;
  inspectMetaData: InspectMetaDataType;
  unit: InspectUnitType;
};

// Unit

export type InspectUnitType = {
  label: string;
  id: string;
};

// Emission by Stages

export type InspectEmissionByStagesType = {
  [key: string]: {
    name?: string;
    entries: InspectEmissionDataRowEntryType[];
  };
};

// Emission Data

export type InspectEmissionDataType = {
  [key: string]: InspectEmissionDataRowType;
};

export type InspectEmissionDataRowType = {
  indicator: string;
  unit: string;
  entries: InspectEmissionDataRowEntryType[];
};

export type InspectEmissionDataRowEntryType = {
  id: string;
  label: string;
  value: number;
};

// Meta Data

export type BaseInspectMetaDataType = {
  dataSource: string;
  description: string;
  descriptions?: TranslatedEpdType;
  name: string;
  names?: TranslatedEpdType;
};

export type InspectMetaDataUnitType = {
  unit: string;
  value: number;
};

export type InspectMetaDataConstructionType = BaseInspectMetaDataType & {
  category: string;
  createdBy: string;
  element: InspectTypeEnum.CONSTRUCTION;
  lastModified?: string;
  massUnit?: InspectMetaDataUnitType;
  model: string;
  isPublic: boolean;
  quantity?: InspectMetaDataUnitType;
  reuse: boolean;
};

export type InspectMetaDataEpdType = BaseInspectMetaDataType & {
  materialName: string;
  category: string;
  createdBy: string;
  dataSourceLink: string;
  declaredUnit?: InspectMetaDataUnitType;
  documentationLink?: string;
  element: InspectTypeEnum.EPD;
  epdStandard: string;
  epdType: string;
  issued: string;
  lastModified?: string;
  lifeSpan: number;
  owner: string;
  ownerLink: string;
  validTo: string;
  weight?: InspectMetaDataUnitType;
};

export type InspectMetaDataMaterialType = BaseInspectMetaDataType & {
  element: InspectTypeEnum.MATERIAL;
  model: string;
  reuse: boolean;
  quantity?: InspectMetaDataUnitType;
};

export type InspectMetaDataTypeType = BaseInspectMetaDataType & {
  category: string;
  classification?: string;
  element: InspectTypeEnum.TYPE;
  model: string;
};

export type InspectMetaDataType =
  | InspectMetaDataConstructionType
  | InspectMetaDataEpdType
  | InspectMetaDataMaterialType
  | InspectMetaDataTypeType;

// ///////////////////
// View / Configuration
// ///////////////////

export type InspectNavigateType = {
  inspectIdentifier: InspectIdentifierType;
  inspectIdentifierId: string;
};

export type InspectToggleType = {
  buildingProjectId?: string;
  configuration: InspectConfigurationType["name"];
  indicatorTypeMappingId?: string;
  inspectIdentifier: InspectIdentifierType;
  inspectIdentifierId: string;
  typeIdentifier: string;
};

export type InspectRequestBaseType = {
  buildingProjectId?: string;
  indicatorTypeMappingId?: string;
  inspectIdentifierId?: string;
};

export enum InspectTypeEnum {
  TYPE = "Type",
  CONSTRUCTION = "Construction",
  MATERIAL = "Material",
  EPD = "EPD",
}

export type InspectIdentifierType = InspectTypeEnum;

export type InspectViewType = {
  populate: (data: InspectRequestBaseType) => Promise<void>;
};

export type InspectConfigurationType = {
  name: string;
  showTree: boolean;
  views: {
    [key in InspectIdentifierType]?: InspectViewType;
  };
};
